import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { magicLinksAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Camera, Milestone, DoorOpen, Loader2, Circle, Clock, CheckCircle2, ImageIcon } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_44f04ad6-a165-46a5-bc68-49188ad32b32/artifacts/hy9ywl5e_WhatsApp%20Image%202026-03-09%20at%2019.34.44.jpeg";

const PHASE_STATUS = {
  not_started: { icon: Circle, color: 'text-gray-400' },
  in_progress: { icon: Clock, color: 'text-blue-500' },
  completed: { icon: CheckCircle2, color: 'text-green-500' },
  on_hold: { icon: Clock, color: 'text-amber-500' },
};

export default function MagicLinkView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    magicLinksAPI.access(token)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Link not found or expired'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="max-w-md w-full mx-4"><CardContent className="py-12 text-center"><h2 className="text-lg font-medium mb-2">Access Denied</h2><p className="text-sm text-muted-foreground">{error}</p></CardContent></Card>
    </div>
  );

  const project = data?.project || {};
  const phases = data?.phases || [];
  const rooms = data?.rooms || [];
  const diary = data?.site_diary || [];
  const completedPhases = phases.filter(p => p.status === 'completed').length;
  const phasePct = phases.length > 0 ? Math.round(completedPhases / phases.length * 100) : 0;

  return (
    <div className="min-h-screen bg-background" data-testid="magic-link-view">
      {/* Header */}
      <header className="border-b bg-card px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img src={LOGO_URL} alt="The Eleventh" className="h-10 w-auto" />
          <span className="text-xs text-muted-foreground uppercase tracking-wider">Client View</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Project Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-light">{project.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{project.city}</p>
          {phases.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-sm mb-1"><span className="text-muted-foreground">Progress</span><span className="font-medium">{phasePct}%</span></div>
              <Progress value={phasePct} className="h-2" />
            </div>
          )}
        </div>

        <Tabs defaultValue="progress">
          <TabsList>
            {data?.permissions?.includes('view_progress') && <TabsTrigger value="progress"><Camera className="h-4 w-4 mr-2" />Progress</TabsTrigger>}
            {data?.permissions?.includes('view_phases') && <TabsTrigger value="phases"><Milestone className="h-4 w-4 mr-2" />Phases</TabsTrigger>}
            {data?.permissions?.includes('view_rooms') && <TabsTrigger value="rooms"><DoorOpen className="h-4 w-4 mr-2" />Rooms</TabsTrigger>}
          </TabsList>

          {/* Progress / Site Diary */}
          <TabsContent value="progress" className="mt-4 space-y-4">
            {diary.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No progress updates yet</CardContent></Card>
            ) : (
              diary.map(entry => (
                <Card key={entry.entry_id}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-medium text-sm">{entry.title}</h4>
                      <span className="text-xs text-muted-foreground">{entry.date}</span>
                    </div>
                    {entry.notes && <p className="text-sm text-muted-foreground mb-3">{entry.notes}</p>}
                    {entry.photos?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {entry.photos.filter(p => p).map((photo, idx) => (
                          <img key={idx} src={photo} alt="" className="h-24 w-24 sm:h-32 sm:w-32 object-cover rounded border" />
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Phases */}
          <TabsContent value="phases" className="mt-4 space-y-1">
            {phases.map((phase, idx) => {
              const config = PHASE_STATUS[phase.status] || PHASE_STATUS.not_started;
              const Icon = config.icon;
              return (
                <div key={phase.phase_id} className="flex items-center gap-3 p-3 rounded hover:bg-muted/30">
                  <div className="flex flex-col items-center">
                    <Icon className={`h-5 w-5 ${config.color}`} />
                    {idx < phases.length - 1 && <div className="w-px h-6 bg-border mt-1" />}
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${phase.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>{phase.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{phase.status?.replace('_', ' ')}</p>
                  </div>
                </div>
              );
            })}
          </TabsContent>

          {/* Rooms */}
          <TabsContent value="rooms" className="mt-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {rooms.map(room => (
                <Card key={room.room_id}>
                  <CardContent className="p-3">
                    <DoorOpen className="h-4 w-4 text-muted-foreground mb-1" />
                    <h4 className="font-medium text-sm">{room.name}</h4>
                    <p className="text-xs text-muted-foreground capitalize">{room.room_type?.replace('_', ' ')}</p>
                    {room.area_sqft && <p className="text-xs text-muted-foreground">{room.area_sqft} sq ft</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
